// src/pages/Insights.js — filterable metrics dashboard (supervisor/admin)
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { getErrorMessage } from '../utils/helpers';
import { PageWrapper } from './Dashboard';

const PRESETS = [
  ['this_month', 'This month'],
  ['last_month', 'Last month'],
  ['last_30', 'Last 30 days'],
  ['last_90', 'Last 90 days'],
  ['ytd', 'Year to date'],
  ['last_year', 'Last year'],
  ['custom', 'Custom'],
];

// Direction is not the same as good — fewer missed rounds is an improvement,
// fewer admissions is not.
function Delta({ value, goodWhenDown = false }) {
  if (value === null || value === undefined || value === 0) {
    return <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>—</span>;
  }
  const up = value > 0;
  const good = goodWhenDown ? !up : up;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: good ? '#0F6E56' : 'var(--red)' }}>
      {up ? '▲' : '▼'} {Math.abs(value)}%
    </span>
  );
}

function Stat({ value, caption, prior, delta, goodWhenDown }) {
  return (
    <div className="stat-card" style={{ textAlign: 'center' }}>
      <div className="num">{value === null || value === undefined ? '—' : value}</div>
      <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{caption}</div>
      <div style={{ marginTop: 4 }}><Delta value={delta} goodWhenDown={goodWhenDown} /></div>
      {prior !== undefined && (
        <div style={{ fontSize: 10, color: '#C3CDE4', marginTop: 1 }}>prev {prior === null ? '—' : prior}</div>
      )}
    </div>
  );
}

function Bar({ pct, color }) {
  return (
    <div style={{ background: '#EEF1F8', borderRadius: 3, height: 8, overflow: 'hidden' }}>
      <div style={{ width: `${pct || 0}%`, background: color, height: 8 }} />
    </div>
  );
}

export default function Insights() {
  const [preset, setPreset] = useState('this_month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [series, setSeries] = useState([]);
  const [detail, setDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const params = useCallback(() => (
    preset === 'custom' && from && to ? `from=${from}&to=${to}` : `preset=${preset}`
  ), [preset, from, to]);

  const load = useCallback(async () => {
    if (preset === 'custom' && (!from || !to)) return;
    setLoading(true); setError('');
    try {
      const [a, s] = await Promise.all([
        api.get(`/analytics?${params()}`),
        api.get(`/analytics/series?year=${new Date().getFullYear()}`).catch(() => ({ data: { months: [] } })),
      ]);
      setData(a.data);
      setSeries(s.data.months || []);
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setLoading(false); }
  }, [preset, from, to, params]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async () => {
    try {
      const r = await api.get(`/analytics/discharges?${params()}`);
      setDetail(r.data); setShowDetail(true);
    } catch (err) { setError(getErrorMessage(err)); }
  };

  const C = data?.current;
  const P = data?.previous;
  const D = data?.deltas || {};

  const maxOcc = Math.max(...series.map(m => m.occupancyPct || 0), 1);

  return (
    <PageWrapper active="insights" title="Insights">
      {error && <div className="alert-bar danger">{error}</div>}

      <div className="card">
        <div className="section-label">Period</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: preset === 'custom' ? 12 : 0 }}>
          {PRESETS.map(([id, label]) => (
            <button key={id} className={`btn btn-sm ${preset === id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPreset(id)}>
              {label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>From</label>
              <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>To</label>
              <input type="date" value={to} onChange={e => setTo(e.target.value)} />
            </div>
          </div>
        )}
        {C && (
          <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 10 }}>
            {C.range.start} to {C.range.end} · {C.range.days} days · compared with {P.range.start} to {P.range.end}
          </div>
        )}
      </div>

      {loading || !C ? <div className="spinner" /> : (
        <>
          <div className="card">
            <div className="section-label">Occupancy</div>
            <div className="stats-grid">
              <Stat value={C.occupancy.pct === null ? '—' : `${C.occupancy.pct}%`} caption="Occupancy"
                prior={P.occupancy.pct === null ? null : `${P.occupancy.pct}%`} delta={D.occupancyPct} />
              <Stat value={C.occupancy.avgCensus} caption="Avg census" prior={P.occupancy.avgCensus} delta={D.avgCensus} />
              <Stat value={C.occupancy.beds} caption="Beds" />
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>
              {C.occupancy.bedDays} of {C.occupancy.available} bed-days filled.
            </div>
          </div>

          <div className="card">
            <div className="section-label">Admissions &amp; discharges</div>
            <div className="stats-grid">
              <Stat value={C.admissions.count} caption="Admits" prior={P.admissions.count} delta={D.admissions} />
              <Stat value={C.discharges.total} caption="Discharges" prior={P.discharges.total} delta={D.discharges} />
              <Stat value={C.discharges.completionRate === null ? '—' : `${C.discharges.completionRate}%`} caption="Completion"
                prior={P.discharges.completionRate === null ? null : `${P.discharges.completionRate}%`} delta={D.completionRate} />
            </div>

            {C.discharges.total > 0 ? (
              <>
                <div style={{ marginTop: 14 }}>
                  {C.discharges.byType.map(t => (
                    <div key={t.code || 'untyped'} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                        <span style={{ color: t.typed ? '#1F2937' : 'var(--red)' }}>{t.label}</span>
                        <span style={{ color: 'var(--gray-400)' }}>
                          {t.count}{t.pct !== null ? ` · ${t.pct}%` : ' · excluded'}
                        </span>
                      </div>
                      {t.pct !== null && <Bar pct={t.pct} color={t.countsAsCompletion ? '#0F6E56' : '#C97840'} />}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4 }}>
                  Shares are of the {C.discharges.typed} discharge{C.discharges.typed === 1 ? '' : 's'} with a type recorded.
                  {C.discharges.untyped > 0 && <span style={{ color: 'var(--red)' }}> {C.discharges.untyped} missing a type, excluded.</span>}
                </div>
                <button className="btn btn-secondary btn-sm" style={{ marginTop: 10 }} onClick={loadDetail}>
                  View the {C.discharges.total} discharge{C.discharges.total === 1 ? '' : 's'}
                </button>
              </>
            ) : <div className="le-time" style={{ marginTop: 10 }}>No discharges in this period.</div>}
          </div>

          <div className="card">
            <div className="section-label">Average length of stay</div>
            <div className="stats-grid">
              <Stat value={C.lengthOfStay.detox.mean ?? '—'} caption="Detox"
                prior={P.lengthOfStay.detox.mean ?? '—'} delta={D.losDetox} />
              <Stat value={C.lengthOfStay.residential.mean ?? '—'} caption="Residential"
                prior={P.lengthOfStay.residential.mean ?? '—'} delta={D.losResidential} />
              <Stat value={C.lengthOfStay.combined.mean ?? '—'} caption="Whole stay"
                prior={P.lengthOfStay.combined.mean ?? '—'} delta={D.losCombined} />
            </div>
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--primary)' }}>
                    {['Level of care', 'Clients', 'Mean', 'Median', 'Range'].map((h, i) => (
                      <th key={h} style={{ padding: '6px 8px', color: '#fff', textAlign: i === 0 ? 'left' : 'right', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[['Detox', C.lengthOfStay.detox], ['Residential', C.lengthOfStay.residential], ['Whole stay', C.lengthOfStay.combined]].map(([label, l], i) => (
                    <tr key={label} style={{ background: i % 2 ? '#F7F9FC' : '#fff' }}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #E5E7EB' }}>{label}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #E5E7EB', color: 'var(--gray-400)' }}>{l.n}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #E5E7EB', fontWeight: 600 }}>{l.mean ?? '—'}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #E5E7EB', color: 'var(--gray-400)' }}>{l.median ?? '—'}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #E5E7EB', color: 'var(--gray-400)' }}>{l.n ? `${l.shortest}–${l.longest}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>
              Days spent at each level of care, for stays that ended in this period. A client who stepped down counts toward both, so those counts can exceed the whole-stay count. Clients still in residence are not counted.
            </div>
          </div>

          <div className="card">
            <div className="section-label">Rounds</div>
            <div className="stats-grid">
              <Stat value={C.rounds.checks} caption="Checks" prior={P.rounds.checks} delta={null} />
              <Stat value={C.rounds.lateEvents} caption="Late" prior={P.rounds.lateEvents} delta={D.lateEvents} goodWhenDown />
              <Stat value={C.rounds.missedEvents} caption="Missed" prior={P.rounds.missedEvents} delta={D.missedEvents} goodWhenDown />
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>
              {C.rounds.onTimePct === null ? 'No checks logged.' : `${C.rounds.onTimePct}% of checks logged on time.`}
            </div>
          </div>

          <div className="card">
            <div className="section-label">Room searches</div>
            <div className="stats-grid">
              <Stat value={C.searches.total} caption="Searches" prior={P.searches.total} delta={D.searches} />
              <Stat value={C.searches.findings} caption="Findings" prior={P.searches.findings} delta={D.findings} />
              <Stat value={C.searches.roomsCovered} caption="Rooms covered" prior={P.searches.roomsCovered} delta={null} />
            </div>
            {C.searches.forCause > 0 && (
              <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>
                {C.searches.forCause} of these were for-cause rather than random.
              </div>
            )}
          </div>

          {series.length > 1 && (
            <div className="card">
              <div className="section-label">Month by month · {new Date().getFullYear()}</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 130, marginTop: 12 }}>
                {series.map(m => (
                  <div key={m.month} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--gray-400)', marginBottom: 2 }}>
                      {m.occupancyPct === null ? '' : `${m.occupancyPct}%`}
                    </div>
                    <div style={{
                      height: Math.max(2, ((m.occupancyPct || 0) / maxOcc) * 90),
                      background: 'var(--primary)', borderRadius: '3px 3px 0 0'
                    }} />
                    <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 4 }}>{m.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 10 }}>Occupancy by month.</div>

              <hr className="divider" />
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--primary)' }}>
                      {['Month', 'Occ', 'Census', 'Admits', 'Disch', 'Compl', 'Late', 'Searches'].map(h => (
                        <th key={h} style={{ padding: '6px 8px', color: '#fff', textAlign: h === 'Month' ? 'left' : 'right', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {series.map((m, i) => (
                      <tr key={m.month} style={{ background: i % 2 ? '#F7F9FC' : '#fff' }}>
                        <td style={{ padding: '5px 8px', borderBottom: '1px solid #E5E7EB' }}>{m.label}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>{m.occupancyPct === null ? '—' : `${m.occupancyPct}%`}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>{m.avgCensus}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>{m.admissions}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>{m.discharges}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>{m.completionRate === null ? '—' : `${m.completionRate}%`}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>{m.lateEvents}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>{m.searches}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {showDetail && detail && (
        <div className="modal-overlay" onClick={() => setShowDetail(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '85vh', overflowY: 'auto' }}>
            <h2>Discharges</h2>
            <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 12 }}>
              {detail.range.start} to {detail.range.end}
              {detail.avgLengthOfStay !== null && ` · average stay ${detail.avgLengthOfStay} days`}
            </div>
            {detail.discharges.map((d, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #F1F3F6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 14 }}>{d.client_name}</span>
                  <span style={{ fontSize: 12, color: d.discharge_type ? (d.counts_as_completion ? '#0F6E56' : '#C97840') : 'var(--red)' }}>
                    {d.type_label}
                  </span>
                </div>
                <div className="le-time">
                  {d.room_number || 'no room'} · discharged {new Date(d.discharge_date).toLocaleDateString()}
                  {d.length_of_stay !== null && ` · ${d.length_of_stay} day stay`}
                </div>
              </div>
            ))}
            <button className="btn btn-secondary" style={{ marginTop: 14 }} onClick={() => setShowDetail(false)}>Close</button>
          </div>
        </div>
      )}
    </PageWrapper>
  );
}
